import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang, useT } from "../i18n";
import { scrollToId } from "../lib/lenis";

const LINKS = [
  ["nav_manifesto", "manifesto"],
  ["nav_menu", "menu"],
  ["nav_gallery", "gallery"],
  ["nav_reviews", "reviews"],
  ["nav_contact", "visit"],
];

export const Nav = () => {
  const t = useT();
  const lang = useLang();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "bg-cream-50/85 backdrop-blur-md border-b border-bergamote-600/15 shadow-sm"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[70px] flex items-center justify-between gap-4">
        <button
          data-testid="nav-brand"
          onClick={() => scrollToId("hero")}
          className="flex items-center group"
        >
          <img
            src="/nav-logo.png"
            alt="la bergamote — Genève · Montreux"
            data-testid="nav-wordmark-logo"
            className="h-12 sm:h-14 w-auto max-w-[56vw] sm:max-w-none object-contain select-none group-hover:opacity-80 transition-opacity duration-300"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map(([key, id]) =>
            id === "menu" ? (
              <a
                key={key}
                href="/_files/ugd/bbb9df_ac87e01cbc2f416ab03b45184d98ab77.pdf?v=13"
                data-testid="nav-link-menu"
                className="text-sm text-bergamote-800 hover:text-saffron-600 transition-colors duration-300 relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-saffron-500 hover:after:w-full after:transition-[width] after:duration-300"
              >
                {t[key]}
              </a>
            ) : (
              <button
                key={key}
                data-testid={`nav-link-${id}`}
                onClick={() => scrollToId(id)}
                className="text-sm text-bergamote-800 hover:text-saffron-600 transition-colors duration-300 relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-saffron-500 hover:after:w-full after:transition-[width] after:duration-300"
              >
                {t[key]}
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle />
          <a
            href="https://www.thefork.com/restaurant/la-bergamote-r752240"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="table-reserve-button"
            className="hidden sm:inline-flex items-center rounded-full bg-saffron-500 text-bergamote-900 text-sm font-semibold px-5 py-2.5 hover:bg-saffron-400 hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
          >
            {t.nav_reserve}
          </a>
        </div>
      </div>
    </motion.header>
  );
};

export const LangToggle = ({ className = "" }) => {
  const lang = useLang();
  return (
    <div
      className={`flex items-center rounded-full border border-bergamote-600/30 bg-cream-50/70 backdrop-blur-sm overflow-hidden ${className}`}
      data-testid="lang-toggle"
    >
      {["en", "fr"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          translate="no"
          onClick={() => window.dispatchEvent(new CustomEvent("set-lang", { detail: l }))}
          className={`notranslate px-3 py-1.5 text-xs font-mono uppercase tracking-widest transition-colors duration-300 ${
            lang === l ? "bg-bergamote-600 text-cream-50" : "text-bergamote-700 hover:text-bergamote-900"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};
